import { Visibility } from '@ail/orders-api'
import { DatabaseReportAdminViewModel, ReportAdminViewModel } from '@ail/reports-api'
import { format } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useGetTenantsQuery } from '../../redux/api/customerApi'
import { useDeleteReportAdminMutation, useGetReportsAdminQuery } from '../../redux/api/reportsApi'
import { Grid, GridColumn } from '../common/Grid/Grid'
import './ReportsAdminPanelFunctional.scss'
import { Loader } from '../common/Loader/Loader'

const REPORT_TYPES = {
  NORMAL: 'Normal reports',
  CHILDREN: 'Children reports',
  STANDARD: 'Standard reports',
}

const REPORT_STATUS = {
  FAILED: 'Failed',
  SUCCESS: 'Success',
}

const SELECTOR_OPTIONS = {
  TYPE: Object.values(REPORT_TYPES).sort(),
  VISIBILITY: [...Object.values(Visibility), 'Not set'].sort(),
  KIND: ['Database', 'Document'].sort(),
  STATUS: Object.values(REPORT_STATUS).sort(),
}

interface GridRowExtentions {
  tenantName?: string
}
type GridRow = ReportAdminViewModel & GridRowExtentions

export const ReportAdminPanelFunctional = () => {
  const { t } = useTranslation('common')
  const reports = useGetReportsAdminQuery()
  const tenants = useGetTenantsQuery()
  const [deleteReport] = useDeleteReportAdminMutation()

  // #region Utilities
  const getTenantName = useCallback(
    (tenantId: string) => {
      return tenants.data?.find((t) => t.id === tenantId)?.name || t('WITHOUT_OWNER')
    },
    [tenants, t]
  )

  const getParentReportName = useCallback(
    (parentId: string) => {
      const parentReport = reports.data?.find((r) => r.id === parentId)
      return parentReport?.name || ''
    },
    [reports]
  )

  const isDatabaseReport = useCallback((report: GridRow): report is GridRow & DatabaseReportAdminViewModel => {
    return report.kind === 'database'
  }, [])

  const handleDeleteReport = useCallback(
    (reportId: string, reportName: string) => {
      if (!window.confirm(`Are you sure to delete "${reportName}" report?`)) {
        return
      }

      deleteReport({ formId: reportId }).unwrap()
    },
    [deleteReport]
  )
  // #endregion

  // #region Data Processing
  const dataRows: GridRow[] = useMemo(() => {
    if (!reports.data) return []
    return reports.data.map((report) => {
      let newReport: GridRow = report

      if (report.tenantId) {
        const tenantName = getTenantName(report.tenantId ?? '')
        newReport = { ...newReport, tenantName }
      }

      if (report.parentId) {
        const parentName = getParentReportName(report.parentId)
        newReport = { ...newReport, name: `${report.name} (${parentName})` }
      }
      return newReport
    })
  }, [getParentReportName, getTenantName, reports])

  const selectorOptionsList = useMemo(() => {
    const uniqueOwners = new Set<string>()

    dataRows.forEach((row) => {
      if (row.tenantName) {
        uniqueOwners.add(row.tenantName)
      }
    })

    return {
      owners: [...uniqueOwners].sort(),
    }
  }, [dataRows])
  // #region Filters
  const filterFns = useMemo(() => {
    return {
      byGlobalString: (report: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true

        const targetString = [report.tenantName, report.name, report.id, report.visibility]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        return splitQuery.every((q) => targetString.includes(q))
      },
      byFormId: (report: GridRow, value: string) => {
        const val = value.trim().toLowerCase()
        return report.id.includes(val)
      },
      byName: (report: GridRow, value: string) => {
        const splitQuery = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
        if (splitQuery.length === 0) return true
        return splitQuery.every((q) => report.name.toLowerCase().includes(q))
      },
      byOwner: (row: GridRow, value?: string) => {
        return row.tenantName === value || !value
      },
      byStatus: (row: GridRow, value?: string) => {
        if (value) {
          return value === 'Failed' ? row.failed : !row.failed
        } else return true
      },
      byKind: (row: GridRow, value?: string) => {
        return row.kind.toLowerCase() === value?.toLowerCase() || !value
      },
      byType: (row: GridRow, value?: string) => {
        if (!value) return true
        switch (value) {
          case REPORT_TYPES.CHILDREN:
            return !!row.parentId
          case REPORT_TYPES.STANDARD:
            return isDatabaseReport(row) && row.isStandardReport
          case REPORT_TYPES.NORMAL:
            return !row.parentId && !(isDatabaseReport(row) && row.isStandardReport)
          default:
            return false
        }
      },
      byVisibility: (row: GridRow, value?: Visibility | string) => {
        if (!value) return true
        return row.visibility ? row.visibility === value : value === 'Not set' ? true : false
      },
    }
  }, [isDatabaseReport])
  // #endregion

  // #region Columns
  const columns = useMemo(() => {
    const cols: GridColumn<GridRow>[] = [
      {
        key: 'id',
        name: 'Report ID',
        width: 320,
        cellClass: 'id',
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byFormId,
          customPlaceholder: 'Search by report ID',
        },
        renderCell: ({ row }) => <div>{row.id}</div>,
      },
      {
        key: 'kind',
        name: 'Type',
        width: 120,
        cellClass: 'type',
        filter: {
          type: 'inline_selector_filter',
          data: SELECTOR_OPTIONS.KIND,
          enableAllOption: true,
          filterFn: filterFns.byKind,
        },
        renderCell: ({ row }) => <div>{row.kind}</div>,
        sortBy: (row) => row.kind,
      },
      {
        key: 'name',
        name: 'Report name',
        minWidth: 250,
        cellClass: 'name',
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byName,
          customPlaceholder: 'Search by name',
        },
        renderCell: ({ row }) => <div>{row.name}</div>,
        sortBy: (row) => row.name,
      },
      {
        key: 'tenant',
        name: 'Tenant',
        width: 200,
        cellClass: 'tenant',
        filter: {
          type: 'inline_selector_filter',
          filterFn: filterFns.byOwner,
          data: selectorOptionsList.owners,
          customPlaceholder: 'Search by name',
          enableAllOption: true,
        },
        renderCell: ({ row }) => <div>{row.tenantName}</div>,
        sortBy: (row) => row.tenantName,
      },

      {
        key: 'created',
        name: 'Date',
        width: 180,
        cellClass: 'date',
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.byGlobalString,
          customPlaceholder: 'Global search',
          position: 'header-right',
        },
        renderCell: ({ row }) => <div>{format(new Date(row.created), 'yyyy-MM-dd hh:mm:ss')}</div>,
        sortBy: (row) => new Date(row.created),
      },
      {
        key: 'schedule',
        name: 'Schedule',
        width: 120,
        cellClass: 'schedule',
        renderCell: ({ row }) => <div>{row.schedule}</div>,
        sortBy: (row) => row.schedule,
      },
      {
        key: 'visibility',
        name: 'Visibility',
        width: 120,
        cellClass: 'visibility',
        filter: {
          type: 'inline_selector_filter',
          filterFn: filterFns.byVisibility,
          data: SELECTOR_OPTIONS.VISIBILITY,
          enableAllOption: true,
        },
        renderCell: ({ row }) => <div>{row.visibility}</div>,
        sortBy: (row) => row.visibility,
      },
      {
        key: 'status',
        name: 'Status',
        width: 100,
        cellClass: 'status',
        filter: {
          type: 'inline_selector_filter',
          filterFn: filterFns.byStatus,
          data: SELECTOR_OPTIONS.STATUS,
          enableAllOption: true,
        },
        renderCell: ({ row }) => {
          const status = row.failed ? 'Failed' : 'Success'
          return <div className={status.toLowerCase()}>{status}</div>
        },
        sortBy: (row) => (row.failed ? 0 : 1),
      },
      {
        key: 'actions',
        name: 'Actions',
        width: 135,
        cellClass: 'actions',
        filter: {
          type: 'inline_selector_filter',
          filterFn: filterFns.byType,
          data: SELECTOR_OPTIONS.TYPE,
          position: 'header-right',
          enableAllOption: true,
        },
        renderCell: ({ row }) => (
          <div className="actions">
            <a onClick={() => handleDeleteReport(row.id, row.name)}>
              <i className="icon-clear" />
            </a>
            <NavLink to={`/ReportsManagement/EditForm/${row.id}`}>
              <i className="icon-edit" />
            </NavLink>
          </div>
        ),
      },
    ]
    return cols
  }, [
    filterFns.byFormId,
    filterFns.byGlobalString,
    filterFns.byKind,
    filterFns.byName,
    filterFns.byOwner,
    filterFns.byStatus,
    filterFns.byType,
    filterFns.byVisibility,
    handleDeleteReport,
    selectorOptionsList.owners,
  ])
  // #endregion

  const initialLoading = reports.isFetching || tenants.isFetching
  // #region Render
  return (
    <Loader className="grid-size" blocking={initialLoading}>
      <Grid
        key="ReportsAdminPanel"
        id="ReportsAdminPanel"
        title="Reports Management Panel"
        icon="icon-reports-man"
        customClass="adminPanelGrid reportsAdmin"
        columns={columns}
        rows={dataRows}
        rowKeyGetter={(row) => row.id}
        renderCustomOption={() => (
          <>
            <NavLink className={'grid-options-widget'} to="/ReportsManagement/EditForm/new">
              New Report
            </NavLink>
          </>
        )}
        onReload={() => reports.refetch()}
        hideExport
        hideReorder
      />
    </Loader>
  )
  // #endregion
}
